import { useEffect, useRef } from "react";
import type { GeoJsonObject } from "geojson";
import type { PathOptions } from "leaflet";
import L from "leaflet";
import { GeoJSON as GeoJSONLayer, useMap } from "react-leaflet";

export interface MapGeoJsonBoundaryLayerProps {
  data?: GeoJsonObject;
  fitOnMount?: boolean;
  style?: PathOptions;
}

const DEFAULT_BOUNDARY_STYLE: PathOptions = {
  color: "#0f766e",
  fillColor: "#14b8a6",
  fillOpacity: 0.12,
  weight: 2,
};

/**
 * Renders a GeoJSON boundary and fits the map to it exactly once per valid
 * boundary. Empty or invalid input renders nothing and never throws.
 */
export const MapGeoJsonBoundaryLayer = ({
  data,
  fitOnMount = true,
  style,
}: MapGeoJsonBoundaryLayerProps) => {
  const map = useMap();
  const fittedData = useRef<GeoJsonObject>();

  useEffect(() => {
    if (!data || !fitOnMount || fittedData.current === data) return;

    let bounds: L.LatLngBounds;
    try {
      bounds = L.geoJSON(data).getBounds();
    } catch {
      return;
    }
    if (!bounds.isValid()) return;

    fittedData.current = data;
    map.fitBounds(bounds, { padding: [24, 24] });
  }, [data, fitOnMount, map]);

  if (!data) return null;

  return (
    <GeoJSONLayer
      data={data}
      style={style ?? DEFAULT_BOUNDARY_STYLE}
    />
  );
};

export default MapGeoJsonBoundaryLayer;
